<template>
    <div class="score-trend-chart">

        <highcharts :options="chartOptions"></highcharts>

    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>

import Vue from 'vue'
import moment from 'moment'
import * as _ from 'lodash'
import Colors from '@/providers/GaugeColors'

/**
 * Component to display trend chart for a Score
 */
export default Vue.extend({
    props: {
        scoreSeriesDef: Object
    },
    computed: {
        chartOptions() {
            const options = this.getBaseOptions()
            if (this.scoreSeriesDef) {
                this.addScoreOptions(options)
            }
            return options
        },
        chartTitle() {
            if (this.scoreSeriesDef) {
                const title = this.$i18n.gt(`LABEL_${this.scoreSeriesDef.score_name}`)
                return title ? title.toUpperCase() : this.scoreSeriesDef.score_name
            } else {
                return ''
            }
        },
        reverseYAxis() {
            if (this.scoreSeriesDef) {
                return this.scoreSeriesDef.reverse_trend_y_axis
            } else {
                return false
            }
        }
    },
    methods: {
        getBaseOptions() {
            return {
                chart: {
                    borderColor: '#fff',
                    type: 'line',
                    height: 80,
                    marginTop: 30,
                    backgroundColor: 'transparent',
                    showAxes: false,
                },
                title: {
                    text: this.chartTitle,
                    align: 'left',
                    floating: true,
                    style: {
                        fontFamily: 'helvetica, arial, sans-serif',
                        fontSize: '14px'
                    }
                },
                credits: {enabled: false},
                tooltip: {enabled: true},
                legend: {enabled: false},

                plotOptions: {
                    series: {
                        color: `rgb(128, 128, 128, 1)`,
                        marker: {
                            symbol: 'circle',
                            radius: 0,
                        },
                        tooltip: {
                            pointFormatter: function () {
                                return `${this.y}`
                            }
                        },
                    },
                    columnrange: {
                        grouping: false
                    }
                },

                xAxis: {
                    visible: false,
                    type: 'datetime',
                    tickAmount: 2,
                    title: null,
                },

                yAxis: [{
                    gridLineWidth: 0,
                    visible: false,
                    title: null,
                    reversed: this.reverseYAxis
                }],

                series: [
                    // main data series
                    {
                        type: 'line',
                        data: [],
                        zones: [{}, {}, {}, {}]
                    },
                    // series showing dot for latest value
                    {
                        type: 'line',
                        data: [],
                        marker: {
                            symbol: 'circle',
                            radius: 4,
                        },
                    },
                    // series for threshold indicators (displayed as a stacked column)
                    {
                        type: 'columnrange',
                        borderWidth: 10,
                        tooltip: {
                            // pointFormatter: function () {
                            //     return 'no'
                            // }
                        },
                    },
                    {type: 'columnrange', borderWidth: 10},
                    {type: 'columnrange', borderWidth: 10},
                    {type: 'columnrange', borderWidth: 10},
                ]
            }
        },
        addScoreOptions(options) {
            // console.log(this.scoreSeriesDef)

            // get main series data - pad or trim to exactly 10 entries,
            // to make the threshold indicator bars come out at a consistent width
            const DATA_POINTS = 10
            let scores = _.take(this.scoreSeriesDef.scores_or_values, DATA_POINTS)
            while (scores.length < 10) {
                scores.push({timestamp: null, score_or_value: null})
            }

            options.series[0].data = _.map(
                _.reverse(scores), // scores are latest-first, so flip order
                (entry) => {
                    return [
                        moment(entry.timestamp).format('M/D/YYYY'),
                        entry.score_or_value
                    ]
                })

            const xCount = options.series[0].data.length
            const minY = +_.minBy(options.series[0].data, (point) => {
                return point[1]
            })[1]
            const maxY = +_.maxBy(options.series[0].data, (point) => {
                return point[1]
            })[1]

            // add last point to "dot" series
            options.series[1].data = new Array(xCount)
            options.series[1].data.fill(null)
            options.series[1].data[xCount - 1] = _.last(options.series[0].data)

            // calculate threshold indicator series based on different options
            const limits = this.scoreSeriesDef.limits
            const thresholds = this.scoreSeriesDef.thresholds
            const higherIsBetter = this.scoreSeriesDef.higher_is_better

            // TODO: stretch max/min for outliers

            const bandColors = Colors.GAUGE_COLORS // see GaugeColors.js
            if (thresholds) {
                for (var i = 0; i <= thresholds.length; i++) {
                    let low = null
                    let high = null
                    let color = null

                    if (i == 0) {
                        low = Math.min(+limits[0], minY)
                        high = +thresholds[0]
                        color = (higherIsBetter ? bandColors[thresholds.length] : bandColors[0])
                    } else if (i == thresholds.length) {
                        low = +thresholds[i - 1]
                        high = Math.max(+limits[1], maxY)
                        color = (higherIsBetter ? bandColors[0] : bandColors[thresholds.length])
                    } else {
                        low = +thresholds[i - 1]
                        high = +thresholds[i]
                        color = (higherIsBetter ? bandColors[thresholds.length - i] : bandColors[i])
                    }

                    options.series[i + 2].data = new Array(xCount + 1)
                    options.series[i + 2].data.fill(null, 0)
                    options.series[i + 2].data[xCount + 1] = {x: xCount + 1, low: low, high: high}
                    options.series[i + 2].color = color
                    options.series[i + 2].borderColor = color
                }
            }

            // console.log(this.scoreSeriesDef.score_name, maxY, this.scoreSeriesDef.limits, this.scoreSeriesDef.thresholds)
        }
    }
})

</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
.score-trend-chart {
    width: 100%;
    overflow: hidden;
    margin: 0;
    margin-left: auto;
    margin-right: auto;
    background-color: transparent;
}
</style>
